import React, { useEffect, useState } from "react";
import { CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, ReferenceArea, Tooltip, XAxis, YAxis, ZAxis, LabelList, Cell } from 'recharts';
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ResetIcon from "@mui/icons-material/RotateLeft";
import MuiTooltip from "@mui/material/Tooltip";
import {sortBy, maxBy, minBy, findIndex, cloneDeep, isEmpty, isNull, isUndefined, replace} from "lodash";

import numberUtil from "@plannel/utils/number-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { gridLine } from "@plannel/components/dashboard/chartTokens";

import '@plannel/scss/components/ChartScatter.scss'
import CodeUtil from "@plannel/pages/utils/code-util";
import { AgTooltipBox, AgTooltipRow } from "@plannel/components/rechart/AgStyleTooltip";
import { FS } from "@plannel/utils/font-util";

const X_MIN_ZOOM = 10;
const Y_MIN_ZOOM = 10;
const DEFAULT_ZOOM = { x1: null, y1: null, x2: null, y2: null };
  
const { colorSet } = ColorUtil;

const ChartScatter = ({ data, scatterConfig, onScatterClick, isRandomColor = false }) => {
  const theme = useTheme();
  const scatterData = cloneDeep(data);
  const [filteredData, setFilteredData] = useState([]);
  const [zoomArea, setZoomArea] = useState(DEFAULT_ZOOM);
  const [xDomain, setXDomain] = useState([]);
  const [yDomain, setYDomain] = useState([]);
  const [xAxisAvg, setXAxisAvg] = useState(0);
  const [yAxisAvg, setYAxisAvg] = useState(0);
  // const [zAxisAvg, setZAxisAvg] = useState(0);
  const [domainHistory, setDomainHistory] = useState([]);
  const [domainIdx, setDomainIdx] = useState(0);
  const [isZooming, setIsZooming] = useState(false);
  const currentUserData = JSON.parse(localStorage.getItem("user"));
  const [isLoading, setisLoading] = useState(false);
  const showZoomBox =
    isZooming &&
    // zoomArea.x1 && zoomArea.x2 && zoomArea.y1 && zoomArea.y2 &&
    !(Math.abs(zoomArea.x1 - zoomArea.x2) < X_MIN_ZOOM) &&
    !(Math.abs(zoomArea.y1 - zoomArea.y2) < Y_MIN_ZOOM);

  const setScatterRandomColor = rows => {
    for (const row of rows) {
      const idx = rows.indexOf(row);
      row.color = colorSet(idx);
    }
  }

  const setScatterData = (domain) => {
    if (isRandomColor) {
      setScatterRandomColor(scatterData);
    }

    for (const row of scatterData) {
      const index = scatterData.indexOf(row);

      for (const data of Object.entries(row)) {
        const key = data[0];

        if (isUndefined(Math.round(data[1])) || isNaN(Math.round(data[1]))) {
          continue;
        }
        if (key === "Nm" || key === "cd") {
          scatterData[index][key] = data[1];
        } else {
          scatterData[index][key] =  Math.round(data[1]);
        }

      }
    }

    const dataPointsInRange = scatterData.filter(
      (d) => d[scatterConfig.xAxisField] >= domain.x1 && d[scatterConfig.xAxisField] <= domain.x2 && d[scatterConfig.yAxisField] >= domain.y1 && d[scatterConfig.yAxisField] <= domain.y2
    );
    setFilteredData(sortBy(dataPointsInRange, scatterConfig.zAxisField).reverse());
  }

  const getAvgAxisValue = (minValue, maxValue) => {
    return (minValue + maxValue) / 2;
  }

  const initDomain = () => {
    if (scatterData.length > 0) {
      const maxX = maxBy(scatterData, scatterConfig.xAxisField)[scatterConfig.xAxisField];
      const minX = minBy(scatterData, scatterConfig.xAxisField)[scatterConfig.xAxisField];
      const maxY = maxBy(scatterData, scatterConfig.yAxisField)[scatterConfig.yAxisField];
      const minY = minBy(scatterData, scatterConfig.yAxisField)[scatterConfig.yAxisField];

      setXDomain([minX - (maxX / 4), maxX * 1.25]);
      setYDomain([minY - (maxY / 4), maxY * 1.25]);

      setXAxisAvg(getAvgAxisValue(minX, maxX));
      setYAxisAvg(getAvgAxisValue(minY, maxY));

      const currentDomain = { x1: minX - (maxX / 4), x2: maxX * 1.25, y1: minY - (maxY / 4), y2: maxY * 1.25 };

      setDomainHistory([currentDomain]);
      setDomainIdx(1);

      setScatterData(currentDomain);
    }
  }

  const pushDomain = (domain) => {
    const history = cloneDeep(domainHistory);
    if (domainIdx === domainHistory.length) {
      history.push(domain);
    } else {
      history.splice(domainIdx);
      history[domainIdx] = domain;
    }
    setXDomain([domain.x1, domain.x2]);
    setYDomain([domain.y1, domain.y2]);

    setXAxisAvg(getAvgAxisValue(domain.x1, domain.x2));
    setYAxisAvg(getAvgAxisValue(domain.y1, domain.y2));

    setDomainHistory(history);
    setDomainIdx(domainIdx + 1);
    setScatterData(domain);
  }

  const backDomain = () => {
    const currentDomainIdx = domainIdx - 1;
    if (!isEmpty(domainHistory) && currentDomainIdx > 0) {
      const pervDomain = domainHistory[currentDomainIdx - 1];
      setXDomain([pervDomain.x1, pervDomain.x2]);
      setYDomain([pervDomain.y1, pervDomain.y2]);

      setXAxisAvg(getAvgAxisValue(pervDomain.x1, pervDomain.x2));
      setYAxisAvg(getAvgAxisValue(pervDomain.y1, pervDomain.y2));

      setDomainIdx(domainIdx - 1);
      setScatterData(pervDomain);
    }
  }

  const forwardDomain = () => {
    const currentDomainIdx = domainIdx - 1;
    if (!isEmpty(domainHistory) && currentDomainIdx < domainHistory.length - 1) {
      const nextDomain = domainHistory[currentDomainIdx + 1];
      setXDomain([nextDomain.x1, nextDomain.x2]);
      setYDomain([nextDomain.y1, nextDomain.y2]);

      setXAxisAvg(getAvgAxisValue(nextDomain.x1, nextDomain.x2));
      setYAxisAvg(getAvgAxisValue(nextDomain.y1, nextDomain.y2));

      setDomainIdx(domainIdx + 1);
      setScatterData(nextDomain);
    }
  }

  const handleZoomOut = () => {
    setIsZooming(false);
    setZoomArea(DEFAULT_ZOOM);

    initDomain();
  }

  const handleMouseDown = (e) => {
    const { xValue, yValue } = e || {};
    if (isNull(e) || !xValue || !yValue) return;
    setIsZooming(true);

    debug("zoom start");
    setZoomArea({ x1: xValue, y1: yValue, x2: xValue, y2: yValue });
  }

  const handleMouseMove = (e) => {
    if (isZooming) {
      debug("zoom selecting");
      setZoomArea((prev) => ({ ...prev, x2: e?.xValue, y2: e?.yValue }));
    }
  }

  const handleMouseUp = (e) => {
    setIsZooming(false);

    if (isNull(e)) return;

    if (isZooming) {
      let { x1, y1, x2, y2 } = zoomArea;

      if (x1 > x2) [x1, x2] = [x2, x1];
      if (y1 > y2) [y1, y2] = [y2, y1];

      if (x2 - x1 < X_MIN_ZOOM || y2 - y1 < Y_MIN_ZOOM) {
        debug("zoom cancel");
      } else {
        if (findIndex(domainHistory, { x1, y1, x2, y2 }) === -1) {
          pushDomain({ x1, y1, x2, y2 });
        }
        debug("zoom stop");
        setZoomArea(DEFAULT_ZOOM);
      }
    }
  }

  const numberFormatter = (number, average, digits = 1) => {
    return numberUtil.formatNumberByAvg(number, average, digits);
  }

  const baseFormatter = (value, field) => {
    const result = numberUtil.formatNumber(value);
    if (CodeUtil.amtCurrencyMeasure.includes(field) && currentUserData?.currencySymbol) {
      if (result < 0) {
        return `-${currentUserData.currencySymbol}${replace(result.toString(), "-", "")}`;
      }
      return currentUserData.currencySymbol + result;
    }
    return result;
  }

  /*
   * 한 점의 여러 축 값을 나열한다(시리즈별 나열이 아님) — 그래서 공용 AgStyleTooltipContent 로
   * 대체하지 못하고 AgTooltipBox/AgTooltipRow 로 조립해 생김새만 맞춘다.
   * 축 값 줄에는 색이 없다 → color 를 안 주면 심볼 자리만 비고 라벨 시작점은 유지된다.
   */
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const itemData = payload[0].payload;

      return (
        <AgTooltipBox>
          {!isEmpty(scatterConfig.parentTagName) && (
            <AgTooltipRow label={scatterConfig.parentTagName} value={itemData[scatterConfig.parentTagField]} />
          )}
          <AgTooltipRow
            color={isRandomColor ? itemData.color : scatterConfig.scatterColor}
            label={scatterConfig.tagName}
            value={itemData[scatterConfig.tagField]}
          />
          <AgTooltipRow label={scatterConfig.xAxisName} value={baseFormatter(itemData[scatterConfig.xAxisField], scatterConfig.xAxisField)} />
          <AgTooltipRow label={scatterConfig.yAxisName} value={baseFormatter(itemData[scatterConfig.yAxisField], scatterConfig.yAxisField)} />
          <AgTooltipRow
            color={scatterConfig.volumeColor}
            label={scatterConfig.zAxisName}
            value={baseFormatter(itemData[scatterConfig.zAxisField])}
          />
        </AgTooltipBox>
      );
    }

    return null;
  };

  const setScatterLabel = ({ payload, size }) => {
    const label = isEmpty(scatterConfig.parentTagName) ? payload[scatterConfig.tagField] : `${payload[scatterConfig.parentTagField]}\n${payload[scatterConfig.tagField]}`
    return size > 1000 || filteredData.length < 3 ? label : '';
  }

  /* 추후 단위값이 필요하다면 설정. */
  // useEffect(() => {
  //   if (!isEmpty(filteredData)) {
  //     const values = map(filteredData, scatterConfig.zAxisField);
  //     const sum = values.reduce((prev, current) => prev + current, 0);
  //     const avg = values.length > 0 ? sum / values.length : 0;

  //     setZAxisAvg(avg);
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [filteredData]);

  useEffect(() => {
    setisLoading(true);
  }, []);

  useEffect(() => {
    if (isLoading && !isEmpty(data) && !isEmpty(scatterConfig) && data?.[0]?.type === scatterConfig?.tagName) {
      initDomain();
    } else {
      setDomainHistory([]);
      setFilteredData([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, scatterConfig]);

  return (
    <Box sx={{ height: "100%", width: "100%", mr: 1 }}>
      <Box sx={{ display: "flex", height:"25px"}}>
        <Box sx={{ ml: 4 }}>
          <MuiTooltip title="Reset">
            <IconButton size="small" onClick={handleZoomOut}>
              <ResetIcon />
            </IconButton>
          </MuiTooltip>
        </Box>
        <Box sx={{ ml: "auto", mr: 2 }}>
          <IconButton size="small" color="error" disabled={domainHistory.length <= 1 || domainIdx <= 1} onClick={backDomain}>
            <MuiTooltip title="Back">
              <ArrowBackIcon />
            </MuiTooltip>
          </IconButton>
          <IconButton size="small" color="info" disabled={domainHistory.length <= 1 || domainIdx === domainHistory.length} onClick={forwardDomain}>
            <MuiTooltip title="Forward">
              <ArrowForwardIcon />
            </MuiTooltip>
          </IconButton>
        </Box>
      </Box>
      <Box height="calc(100% - 25px)">
        <ResponsiveContainer className="plot-container" height="99%">
          <ScatterChart
            data={filteredData}
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <CartesianGrid stroke={gridLine(theme)} />
            <XAxis
              type="number"
              dataKey={scatterConfig.xAxisField}
              name={scatterConfig.xAxisName}
              domain={xDomain}
              tickFormatter={number => numberFormatter(number, xAxisAvg, 0)}
              label={{ value: scatterConfig.xAxisName, offset: 0, position: "bottom" }} />
            <YAxis
              type="number"
              dataKey={scatterConfig.yAxisField}
              name={scatterConfig.yAxisName}
              domain={yDomain}
              tickFormatter={number => numberFormatter(number, yAxisAvg, 0)}
              allowDataOverflow
              label={{ value: scatterConfig.yAxisName, offset: 0, position: "left", angle: -90 }} />
            <ZAxis
              type="number"
              dataKey={scatterConfig.zAxisField}
              range={[300, 5000]}
              name={scatterConfig.zAxisName}
              allowDataOverflow
            />
            <Tooltip content={CustomTooltip} cursor={{ strokeDasharray: "3 3" }} />
            {showZoomBox && (
              <ReferenceArea
                x1={zoomArea?.x1}
                x2={zoomArea?.x2}
                y1={zoomArea?.y1}
                y2={zoomArea?.y2}
              />
            )}
            <Scatter data={filteredData} fill={scatterConfig?.scatterColor} opacity={0.5} shape="circle" onClick={e => onScatterClick(e)}>
              <LabelList valueAccessor={setScatterLabel} position="right" style={{ fontSize: FS.H2, fontStyle: 'italic' }} />
              {
                isRandomColor ?
                  filteredData.map((entry) => {
                    return <Cell key={entry.cd} fill={entry.color} />
                  }) : ""
              }
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default React.memo(ChartScatter);